import { useState, useEffect } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { Sun, Moon, X } from 'lucide-react';

export const ThemeSwitchIndicator = () => {
  const { isDark, isManualOverride } = useTheme();
  const [timeUntilSwitch, setTimeUntilSwitch] = useState(null);
  const [showIndicator, setShowIndicator] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Don't show if user has manually overridden theme
    if (isManualOverride) {
      setShowIndicator(false);
      return;
    }

    const calculateTimeUntilSwitch = () => {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinutes = now.getMinutes();
      
      let nextSwitchHour;
      let nextTheme;
      
      // Dark mode: 6PM (18:00) to 6AM (06:00)
      // Light mode: 6AM (06:00) to 6PM (18:00)
      if (currentHour >= 18 || currentHour < 6) {
        // Currently dark mode, switch to light at 6AM
        nextSwitchHour = 6;
        nextTheme = 'light';
      } else {
        // Currently light mode, switch to dark at 6PM
        nextSwitchHour = 18;
        nextTheme = 'dark';
      }

      // Calculate minutes until switch
      let hoursUntil, minutesUntil;
      
      if (currentHour >= nextSwitchHour) {
        // Switch is tomorrow
        hoursUntil = (24 - currentHour) + nextSwitchHour;
      } else {
        hoursUntil = nextSwitchHour - currentHour;
      }
      
      // Adjust for current minutes
      minutesUntil = (hoursUntil * 60) - currentMinutes;
      
      // Only show indicator if within 30 minutes of switch
      const showThreshold = 30; // minutes
      
      if (minutesUntil <= showThreshold && minutesUntil > 0) {
        setTimeUntilSwitch({
          minutes: minutesUntil,
          nextTheme
        });
        setShowIndicator(true);
      } else {
        setShowIndicator(false);
      }
    };

    // Calculate immediately
    calculateTimeUntilSwitch();
    
    // Update every minute
    const interval = setInterval(calculateTimeUntilSwitch, 60000);
    
    return () => clearInterval(interval);
  }, [isManualOverride]);

  // Reset dismissed state when indicator hides
  useEffect(() => {
    if (!showIndicator) {
      setDismissed(false);
    }
  }, [showIndicator]);

  if (!showIndicator || dismissed || isManualOverride) return null;

  const isNearSwitch = timeUntilSwitch?.minutes <= 5;

  return (
    <div 
      className={`fixed bottom-24 md:bottom-6 right-6 z-40 animate-in slide-in-from-right-10 duration-500`}
    >
      <div 
        className={`flex items-center gap-3 px-4 py-3 rounded-2xl backdrop-blur-lg shadow-lg border transition-all ${
          isDark 
            ? 'bg-[#233842]/90 border-white/10 text-white' 
            : 'bg-white/90 border-ux-charcoal/10 text-ux-charcoal'
        } ${isNearSwitch ? 'animate-pulse' : ''}`}
      >
        {/* Icon */}
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
          timeUntilSwitch?.nextTheme === 'dark' 
            ? 'bg-gradient-to-br from-indigo-500 to-purple-600' 
            : 'bg-gradient-to-br from-amber-400 to-orange-500'
        }`}>
          {timeUntilSwitch?.nextTheme === 'dark' ? (
            <Moon className="w-5 h-5 text-white" />
          ) : (
            <Sun className="w-5 h-5 text-white" />
          )}
        </div>

        {/* Text */}
        <div className="pr-2">
          <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
            {timeUntilSwitch?.nextTheme === 'dark' ? 'Dark mode' : 'Light mode'} in{' '}
            <span className="text-ux-cyan font-bold">
              {timeUntilSwitch?.minutes} min
            </span>
          </p>
          <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
            Auto theme switch at {timeUntilSwitch?.nextTheme === 'dark' ? '6 PM' : '6 AM'}
          </p>
        </div>

        {/* Dismiss button */}
        <button
          onClick={() => setDismissed(true)}
          className={`p-1 rounded-lg hover:bg-white/10 transition-colors ${
            isDark ? 'text-white/50 hover:text-white' : 'text-ux-charcoal/50 hover:text-ux-charcoal'
          }`}
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ThemeSwitchIndicator;
